import { useRef, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Film, Image as ImageIcon, Pencil, Scissors, Search, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReelDialog } from "@/components/ReelDialog";
import { api, errMsg, fetcher } from "@/lib/api";

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export const VideotecaPanel = () => {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [form, setForm] = useState({ title: "", description: "", caption: "", credit: "",
                                     category: "Archivo", tags: "" });
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [poster, setPoster] = useState(null);
  const [reel, setReel] = useState(null);
  const fileRef = useRef(null);

  const key = `/videos/library?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}&limit=30`;
  const { data, mutate } = useSWR(key, fetcher);
  const { data: cats } = useSWR("/videos/library/categories", fetcher);
  const { data: outlets } = useSWR("/outlets", fetcher);
  const categories = cats?.categories || [];

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const { data: init } = await api.post("/videos/library/init", {
        filename: file.name, size: file.size, content_type: file.type || "video/mp4",
      });
      const size = init.chunk_size;
      const total = Math.ceil(file.size / size);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * size, (i + 1) * size), "part");
        await api.post("/videos/library/chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await api.post("/videos/library/complete", {
        upload_id: init.upload_id,
        title: form.title || file.name,
        description: form.description, caption: form.caption, credit: form.credit,
        category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Video guardado en la videoteca");
      setForm({ ...form, title: "", description: "", caption: "", tags: "" });
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      await api.put(`/videos/library/${edit.id}`, {
        title: edit.title, description: edit.description, caption: edit.caption,
        credit: edit.credit, category: edit.category,
        tags: (edit.tagsText || "").split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Ficha del video actualizada");
      setEdit(null);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const makePoster = async (mode) => {
    try {
      await api.post(`/videos/library/${poster.id}/poster`, {
        image_url: poster.imageUrl, outlet_id: poster.outletId || null, mode,
      });
      toast.success(mode === "none" ? "Carátula quitada" : "Carátula generada");
      setPoster(null);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Quitar «${item.title}» de la videoteca?`)) return;
    try {
      await api.delete(`/videos/library/${item.id}`);
      toast.success("Video eliminado");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="videoteca-panel">
      <div className="border border-border bg-card p-5 mb-6">
        <div className="kicker mb-3 flex items-center gap-2">
          <Film className="h-3.5 w-3.5 text-primary" />Subir un video de la agencia
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Título (WordPress)">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="video-upload-title" />
          </Field>
          <Field label="Pie del video">
            <Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
                   data-testid="video-upload-caption" />
          </Field>
          <Field label="Crédito o fuente">
            <Input value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })}
                   data-testid="video-upload-credit" />
          </Field>
          <Field label="Categoría">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 border border-input bg-background px-3 text-sm rounded-md"
                    data-testid="video-upload-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Etiquetas (coma)">
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                   data-testid="video-upload-tags" />
          </Field>
          <Field label="Descripción (WordPress)">
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   data-testid="video-upload-description" />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <Button onClick={() => fileRef.current?.click()} disabled={busy}
                  data-testid="video-upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            {busy ? `Subiendo… ${progress}%` : "Elegir archivo (hasta 200 MB)"}
          </Button>
          <span className="text-xs text-muted-foreground">
            MP4, WebM o MOV. Se sube por partes, así que puede tardar unos minutos.
          </span>
        </div>
        {busy && (
          <div className="h-1.5 bg-muted mt-3" data-testid="video-upload-progress">
            <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
        <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime"
               className="hidden" onChange={(e) => upload(e.target.files?.[0])}
               data-testid="video-file-input" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                 placeholder="Buscar por título, pie o etiqueta" data-testid="video-search-input" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="h-10 border border-input bg-background px-3 text-sm rounded-md"
                data-testid="video-filter-category">
          <option value="">Todas las categorías</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-muted-foreground" data-testid="video-total">
          {data?.total || 0} videos
        </span>
      </div>

      {!data?.items?.length ? (
        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground"
             data-testid="videoteca-empty">
          <Film className="h-6 w-6 mx-auto mb-3 opacity-60" />
          Todavía no hay videos en la videoteca.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4" data-testid="videoteca-grid">
          {data.items.map((it) => (
            <div key={it.id} className="border border-border bg-card" data-testid={`video-item-${it.id}`}>
              <video src={it.url} poster={it.poster_url || undefined} controls preload="metadata"
                     className={`bg-black ${it.reel_of ? "mx-auto h-72 aspect-[9/16]"
                                                       : "w-full aspect-video"}`} />
              <div className="p-3">
                <p className="text-sm font-medium truncate" title={it.title}>{it.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">{it.caption}</p>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="secondary" className="text-[10px]">{it.category}</Badge>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => setReel(it)}
                            title="Recorte vertical con subtítulos"
                            data-testid={`video-reel-${it.id}`}>
                      <Scissors className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => setPoster({ id: it.id, imageUrl: "", outletId: "" })}
                            title="Carátula con la identidad del medio"
                            data-testid={`video-poster-${it.id}`}>
                      <ImageIcon className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => setEdit({ ...it, tagsText: (it.tags || []).join(", ") })}
                            data-testid={`video-edit-${it.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                            onClick={() => remove(it)} data-testid={`video-delete-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {reel && <ReelDialog video={reel} onClose={() => setReel(null)} onDone={mutate} />}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="grid-cols-1 max-w-lg" data-testid="video-edit-dialog">
          <DialogHeader><DialogTitle>Ficha del video</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Field label="Título">
                <Input value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                       data-testid="video-edit-title" />
              </Field>
              <Field label="Pie del video">
                <Input value={edit.caption || ""} onChange={(e) => setEdit({ ...edit, caption: e.target.value })}
                       data-testid="video-edit-caption" />
              </Field>
              <Field label="Descripción">
                <Textarea rows={3} value={edit.description || ""}
                          onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                          data-testid="video-edit-description" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Crédito">
                  <Input value={edit.credit || ""} onChange={(e) => setEdit({ ...edit, credit: e.target.value })}
                         data-testid="video-edit-credit" />
                </Field>
                <Field label="Categoría">
                  <select value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                          className="w-full h-10 border border-input bg-background px-3 text-sm rounded-md"
                          data-testid="video-edit-category">
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Etiquetas">
                <Input value={edit.tagsText || ""} onChange={(e) => setEdit({ ...edit, tagsText: e.target.value })}
                       data-testid="video-edit-tags" />
              </Field>
              <Button onClick={save} className="w-full" data-testid="video-edit-save">Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!poster} onOpenChange={(o) => !o && setPoster(null)}>
        <DialogContent className="grid-cols-1 max-w-lg" data-testid="video-poster-dialog">
          <DialogHeader><DialogTitle>Carátula del video</DialogTitle></DialogHeader>
          {poster && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Indique la imagen base (un fotograma o la foto de la nota). Se le aplica el logo del
                medio y el sello «VIDEO», igual que a las tarjetas de redes.
              </p>
              <Field label="Imagen base (URL)">
                <Input value={poster.imageUrl}
                       onChange={(e) => setPoster({ ...poster, imageUrl: e.target.value })}
                       placeholder="https://…" data-testid="video-poster-url" />
              </Field>
              <Field label="Medio">
                <select value={poster.outletId}
                        onChange={(e) => setPoster({ ...poster, outletId: e.target.value })}
                        className="w-full h-10 border border-input bg-background px-3 text-sm rounded-md"
                        data-testid="video-poster-outlet">
                  <option value="">Sin medio</option>
                  {(outlets || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => makePoster("logo")} disabled={!poster.imageUrl}
                        data-testid="video-poster-logo">Con logo y sello</Button>
                <Button variant="outline" onClick={() => makePoster("badge")} disabled={!poster.imageUrl}
                        data-testid="video-poster-badge">Solo el sello</Button>
                <Button variant="ghost" onClick={() => makePoster("none")}
                        data-testid="video-poster-none">Sin carátula</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
